import React, { useContext } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { EvaluationContext } from "../stores/EvaluationContext";
import { isCriterionFilledOut } from "../utils/completionProgress";
import LinkToGuidance from "./LinkToGuidance";
import Component from "./Component";
import CompletionCheckMark from "./CompletionCheckMark";
import { anchorSvg } from "./HeaderWithAnchor";
import "./Criteria.css";

/**
 * Single criterion within a chapter. Ported from GSA openacr-editor Criteria.svelte.
 * Uses <details>/<summary> for expand/collapse so ExpandCollapseAll works; when collapsed only the header shows.
 */
export default function Criteria({
  chapterId,
  viewInReportTo,
  id,
  handle,
  url,
  components = [],
}) {
  const { evaluation } = useContext(EvaluationContext);
  const disabled = evaluation?.chapters?.[chapterId]?.disabled;
  const filledOut = !disabled && isCriterionFilledOut(chapterId, id, evaluation, components);

  return (
    <details id={id} className="criteria criteria-details mb-8">
      <summary className="flex items-center">
        <Link
          to={`#${id}-editor`}
          id={`${id}-editor`}
          className="criteria-anchor-link"
          aria-label="Anchor link"
          onClick={(e) => e.stopPropagation()}
        >
          {anchorSvg}
        </Link>
        <span className="criteria-summary-inner">
          <h2 className="criteria-title">{id}: {handle}</h2>
          {filledOut && (
            <CompletionCheckMark className="criteria-check" />
          )}
        </span>
      </summary>
      <div className="criteria-content mt-2">
        {!disabled && viewInReportTo && (
          <p className="observation__meta text-sm">
            <Link to={viewInReportTo} className="usa-link link-to-guidance text-sm">
              View in Report
            </Link>
          </p>
        )}

        <p className="text-xs">
          {url ? (
            <LinkToGuidance href={url} showInfoIcon>Implementing {id}: {handle}</LinkToGuidance>
          ) : (
            <>Implementing {id}: {handle}</>
          )}
        </p>

        {Array.isArray(components) && components.length > 0 && (
          <div className="components mt-2">
            {components.map((c) => (
              <Component
                key={c.name || c}
                chapterId={chapterId}
                criteria={id}
                component={typeof c === "string" ? c : c.name}
              />
            ))}
          </div>
        )}
      </div>
    </details>
  );
}

const componentItemType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({ name: PropTypes.string }),
]);

Criteria.propTypes = {
  chapterId: PropTypes.string.isRequired,
  /** In-app Report route + hash (same row id as Report preview). Not an external spec URL. */
  viewInReportTo: PropTypes.string,
  id: PropTypes.string.isRequired,
  handle: PropTypes.string.isRequired,
  url: PropTypes.string,
  components: PropTypes.arrayOf(componentItemType),
};

Criteria.defaultProps = {
  viewInReportTo: null,
  url: null,
  components: [],
};
